import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '@/components/ui/table';
import { 
  Loader2, 
  MessageSquare, 
  TicketIcon, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  ThumbsUp, 
  ThumbsDown,
  FolderIcon,
  ImageIcon,
  User,
  Banknote,
  Gamepad2,
  Bug,
  HelpCircle
} from 'lucide-react';

interface TicketsListProps {
  onCreateTicket?: () => void;
}

// Get category icon based on category name
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'profile':
      return <User className="h-4 w-4" />;
    case 'account':
      return <FolderIcon className="h-4 w-4" />;
    case 'coin_purchase':
      return <Banknote className="h-4 w-4" />;
    case 'gameplay':
      return <Gamepad2 className="h-4 w-4" />;
    case 'bugs':
      return <Bug className="h-4 w-4" />;
    case 'other':
    default:
      return <HelpCircle className="h-4 w-4" />;
  }
};

// Get category label
const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'profile':
      return 'Profile';
    case 'account':
      return 'Account';
    case 'coin_purchase':
      return 'Coin Purchase';
    case 'gameplay':
      return 'Gameplay';
    case 'bugs':
      return 'Bugs';
    case 'other':
    default:
      return 'Other';
  }
};

export default function TicketsList({ onCreateTicket }: TicketsListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [userFeedback, setUserFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Fetch the user's tickets
  const { data: ticketsData, isLoading, isError } = useQuery({
    queryKey: ['/api/support/tickets'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/support/tickets');
      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }
      return response.json();
    },
  });
  
  // Format date to a readable string
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Status badge color
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="default">Open</Badge>;
      case 'in_progress':
        return <Badge variant="secondary">In Progress</Badge>;
      case 'closed':
        return <Badge variant="outline">Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Priority badge color
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'medium':
        return <Badge>Medium</Badge>;
      case 'low':
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  // Category badge
  const getCategoryBadge = (category: string) => {
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {getCategoryIcon(category)}
        {getCategoryLabel(category)}
      </Badge>
    );
  };

  // Event handlers
  const handleViewResponse = (ticket: any) => {
    setSelectedTicket(ticket);
    setFeedbackType(null);
    setUserFeedback('');
    setSelectedImage(null);
  };
  
  const handleClose = () => {
    setSelectedTicket(null);
    setFeedbackType(null);
    setUserFeedback('');
    setSelectedImage(null);
  };
  
  const handleFeedbackTypeSelect = (type: 'positive' | 'negative') => {
    setFeedbackType(type);
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };
  
  // Submit feedback mutation
  const feedbackMutation = useMutation({
    mutationFn: async (data: { ticketId: number, feedbackType: 'positive' | 'negative', feedbackContent: string }) => {
      const response = await apiRequest('POST', `/api/support/tickets/${data.ticketId}/feedback`, {
        feedbackType: data.feedbackType,
        feedbackContent: data.feedbackContent
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit feedback');
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate the tickets query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['/api/support/tickets'] });
      
      toast({
        title: "Feedback submitted",
        description: "Thank you for your feedback on this support ticket.",
      });
      
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    }
  });

  const submitFeedback = () => {
    if (!selectedTicket || !feedbackType) return;
    
    feedbackMutation.mutate({
      ticketId: selectedTicket.id,
      feedbackType,
      feedbackContent: userFeedback
    });
  };
  
  // Ensure tickets is always an array
  const tickets = Array.isArray(ticketsData) ? ticketsData : [];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card className="border-destructive/20 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-destructive">Error loading tickets</CardTitle>
          <CardDescription>
            We couldn't load your support tickets. Please try again later.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
            className="w-full"
          >
            Retry
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // Empty state
  if (!tickets || tickets.length === 0) {
    return (
      <div className="py-10 flex flex-col items-center justify-center text-center">
        <div className="bg-muted/30 rounded-full p-5 mb-4">
          <TicketIcon className="h-12 w-12 text-muted-foreground opacity-40" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Support Tickets</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          You haven't submitted any support tickets yet. Need help? Create a new ticket and our support team will assist you.
        </p>
        <Button 
          className="gap-2" 
          size="sm" 
          onClick={onCreateTicket}
        >
          <MessageSquare className="h-4 w-4" />
          Create New Ticket
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold">{tickets.length}</div>
            <div className="text-sm text-muted-foreground">Total Tickets</div>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold">{tickets.filter(t => t.status === 'open').length}</div>
            <div className="text-sm text-muted-foreground">Open</div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold">{tickets.filter(t => t.status === 'in_progress').length}</div>
            <div className="text-sm text-muted-foreground">In Progress</div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold">{tickets.filter(t => t.status === 'closed').length}</div>
            <div className="text-sm text-muted-foreground">Resolved</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tickets Table */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Subject</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Response</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.map((ticket: any) => (
              <TableRow key={ticket.id} className="group cursor-pointer hover:bg-muted/50" onClick={() => handleViewResponse(ticket)}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {ticket.status === 'closed' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : ticket.status === 'in_progress' ? (
                      <Clock className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    )}
                    <span className="truncate max-w-[300px]">
                      {ticket.subject}
                      {ticket.imageUrls && ticket.imageUrls.length > 0 && (
                        <ImageIcon className="h-3 w-3 ml-2 inline-block text-muted-foreground" />
                      )}
                    </span>
                  </div>
                </TableCell>
                <TableCell>{getCategoryBadge(ticket.category || 'other')}</TableCell>
                <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                <TableCell className="text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(ticket.createdAt)}
                  </div>
                </TableCell>
                <TableCell>
                  {ticket.adminResponse ? (
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3 text-primary" />
                      <span className="text-sm">{ticket.feedbackType ? (
                        ticket.feedbackType === 'positive' ? (
                          <span className="text-green-500 flex items-center gap-1">
                            <ThumbsUp className="h-3 w-3" /> Feedback sent
                          </span>
                        ) : (
                          <span className="text-red-500 flex items-center gap-1">
                            <ThumbsDown className="h-3 w-3" /> Feedback sent
                          </span>
                        )
                      ) : 'Responded'}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Awaiting response
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      {/* Ticket Response Dialog */}
      <Dialog open={selectedTicket !== null} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {selectedTicket?.status === 'closed' ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : selectedTicket?.status === 'in_progress' ? (
                <Clock className="h-5 w-5 text-blue-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              {selectedTicket?.subject}
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="flex items-center gap-1">
                <TicketIcon className="h-3.5 w-3.5" />
                Ticket #{selectedTicket?.id}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Created {selectedTicket && formatDate(selectedTicket.createdAt)}
              </span>
              {selectedTicket?.category && (
                <span>{getCategoryBadge(selectedTicket.category)}</span>
              )}
              {selectedTicket?.status && (
                <span>{getStatusBadge(selectedTicket.status)}</span>
              )}
              {selectedTicket?.priority && (
                <span>{getPriorityBadge(selectedTicket.priority)}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Issue description */}
            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Your Issue
              </h3>
              <div className="text-sm p-4 rounded-md bg-muted/40 whitespace-pre-line border border-muted">
                {selectedTicket?.description}
              </div>
            </div>
            
            {/* Images gallery */}
            {selectedTicket?.imageUrls && selectedTicket.imageUrls.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  Attached Images
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedTicket.imageUrls.map((imageUrl: string, index: number) => (
                    <div 
                      key={index} 
                      className="relative cursor-pointer border rounded-md overflow-hidden hover:opacity-90"
                      onClick={() => handleImageClick(imageUrl)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Image ${index + 1}`}
                        className="w-20 h-20 object-cover"
                      />
                    </div>
                  ))}
                </div>
                
                {/* Image preview dialog */}
                {selectedImage && (
                  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
                    <div className="max-w-3xl max-h-full">
                      <img
                        src={selectedImage}
                        alt="Full size"
                        className="max-w-full max-h-full object-contain"
                      />
                      <Button 
                        className="absolute top-4 right-4" 
                        variant="destructive" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(null);
                        }}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Admin response */}
            {selectedTicket?.adminResponse ? (
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-1 text-primary">
                  <MessageSquare className="h-4 w-4" />
                  Admin Response
                </h3>
                <div className="text-sm p-4 rounded-md bg-primary/5 border border-primary/20 text-foreground whitespace-pre-line">
                  {selectedTicket?.adminResponse}
                </div>
                
                {/* Show existing feedback or feedback form */}
                {selectedTicket?.feedbackType ? (
                  <div className="mt-4 p-4 rounded-md border bg-muted/20">
                    <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
                      {selectedTicket.feedbackType === 'positive' ? (
                        <>
                          <ThumbsUp className="h-4 w-4 text-green-500" />
                          <span className="text-green-600">Your Positive Feedback</span>
                        </>
                      ) : (
                        <>
                          <ThumbsDown className="h-4 w-4 text-red-500" />
                          <span className="text-red-600">Your Negative Feedback</span>
                        </>
                      )}
                    </h3>
                    
                    {selectedTicket.feedbackContent ? (
                      <div className="text-sm p-3 rounded-md bg-background/80 whitespace-pre-line border">
                        {selectedTicket.feedbackContent}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground italic">
                        You didn't provide additional feedback details.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="pt-4 mt-2 border-t">
                    <h3 className="text-sm font-medium mb-2">Was this response helpful?</h3>
                    <div className="flex items-center gap-3 mb-3">
                      <Button 
                        variant={feedbackType === 'positive' ? "default" : "outline"}
                        size="sm"
                        className={`flex items-center gap-1 ${feedbackType === 'positive' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        onClick={() => handleFeedbackTypeSelect('positive')}
                      >
                        <ThumbsUp className="h-4 w-4" />
                        Yes
                      </Button>
                      <Button 
                        variant={feedbackType === 'negative' ? "default" : "outline"}
                        size="sm"
                        className={`flex items-center gap-1 ${feedbackType === 'negative' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        onClick={() => handleFeedbackTypeSelect('negative')}
                      >
                        <ThumbsDown className="h-4 w-4" />
                        No
                      </Button>
                    </div>
                    
                    {feedbackType && (
                      <div className="space-y-3">
                        <Textarea 
                          placeholder="Tell us more about your experience..." 
                          value={userFeedback}
                          onChange={(e) => setUserFeedback(e.target.value)}
                          className="min-h-[80px] text-foreground placeholder:text-muted-foreground/70 bg-background border-input"
                        />
                        <Button 
                          size="sm" 
                          onClick={submitFeedback}
                          disabled={feedbackMutation.isPending}
                          className="flex items-center gap-1"
                        >
                          {feedbackMutation.isPending ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>Submit Feedback</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-6 rounded-md bg-muted/40 border border-dashed">
                <Clock className="h-8 w-8 text-muted-foreground/60 mb-2" />
                <h3 className="text-sm font-medium">Waiting for Response</h3>
                <p className="text-xs text-muted-foreground">Our support team will respond to your ticket soon.</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}